/**
 * ROLE COLUMNS — "one column per role" for the List view, laid out from the
 * tenant's own role configuration instead of a hardcoded role list.
 *
 * The pattern (data comes from `/control/roles/role-pipeline/lite`, see the
 * server's `describeRoleWorkPattern`):
 *
 *   1. WHICH roles get a column. The roles the user ticked under Show/hide →
 *      Roles win. With NOTHING ticked the list shows every WORKING role — a
 *      role that holds at least one stage at a personal level (edit/view/own),
 *      i.e. someone who is actually assigned leads and deals. Admin and purely
 *      supervisory roles have no working stage and get no column, which is
 *      why "Super Admin" never shows up beside every lead.
 *
 *   2. WHERE the column sits. The roles that work the FIRST pipeline stage
 *      (intake — the Counsellor-type roles that hold the lead itself) belong
 *      with the lead's own columns, right after Branch. Every role that
 *      starts later in the journey (pre-application / application / visa
 *      teams) is a deal-stage role and sits at the END, after the deal's
 *      columns (Country / University / Course / Intake) and Stage / Status.
 *      This is the intake-stage rule, not the wizard's inbound toggles —
 *      on Skymark every team role "accepts leads", which put the visa team
 *      next to the counsellor (2026-09-03).
 *
 *   3. IN WHAT ORDER. Journey order: by the pipeline order of the FIRST stage
 *      the role works, then role hierarchy, then name. So Counsellor (enquiry)
 *      → pre-application → application → pre-visa → visa, on any tenant that
 *      configured its roles per stage — and the same rule keeps holding when
 *      a stage or a role is added.
 *
 * Shared by the leads list, the stage-route / favorite-view list, the list
 * fetchers (`roleShow` — which roles the server attaches `cardRoles` for) and
 * the Show/hide menus, so every surface agrees on the same set and order.
 */

export interface RoleCatalogEntry {
  id: number | string;
  name?: string | null;
  hierarchy?: number | null;
  acceptsLead?: boolean;
  acceptsChild?: boolean;
  personalStageCount?: number;
  firstStageOrder?: number | null;
  /**
   * The viewer excluded this role under role hierarchy. It is still LISTED in
   * the Show/hide picker so anyone can add its column (user directive
   * 2026-09-04), but it never earns a column on its own — see
   * `defaultRoleColumnsCatalog`.
   */
  isExcluded?: boolean;
}

export type RoleColumnLevel = "lead" | "deal";

export interface RoleColumn {
  id: number;
  /** Stable React key / column id — `role-<id>`. */
  key: string;
  label: string;
  level: RoleColumnLevel;
}

export interface RoleColumnLayout {
  /** Lead-assigned roles — rendered with the lead's own columns. */
  lead: RoleColumn[];
  /** Deal-only roles — rendered after the deal (Country / Course) columns. */
  deal: RoleColumn[];
  /** Every column, journey-ordered. */
  all: RoleColumn[];
  /** Ascending ids — the `roleShow` request param (stable for caching). */
  ids: number[];
  /** True when nothing is ticked and the working-role default is in force. */
  isDefault: boolean;
}

const EMPTY_LAYOUT: RoleColumnLayout = {
  lead: [],
  deal: [],
  all: [],
  ids: [],
  isDefault: true,
};

function roleId(r: RoleCatalogEntry): number {
  const n = Number(r?.id);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function orderOf(r: RoleCatalogEntry): number {
  const v = r.firstStageOrder;
  return typeof v === "number" && Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
}

function hierarchyOf(r: RoleCatalogEntry): number {
  const v = r.hierarchy;
  return typeof v === "number" && Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
}

/** Journey order: first working stage → hierarchy → name → id. */
export function compareRolesByJourney(
  a: RoleCatalogEntry,
  b: RoleCatalogEntry,
): number {
  const o = orderOf(a) - orderOf(b);
  if (o !== 0) return o;
  const h = hierarchyOf(a) - hierarchyOf(b);
  if (h !== 0) return h;
  const n = String(a.name ?? "").localeCompare(String(b.name ?? ""));
  if (n !== 0) return n;
  return roleId(a) - roleId(b);
}

/** A role somebody WORKS in — it holds at least one stage at a personal level. */
export function isWorkingRole(r: RoleCatalogEntry): boolean {
  return (r.personalStageCount ?? 0) > 0;
}

/**
 * Roles working the INTAKE stage (the earliest first-stage order among the
 * tenant's working roles) hold the lead itself and sit with the lead's
 * columns; every later-starting role is deal-stage and sits at the end. A
 * role with no working stage at all is treated as deal-stage.
 */
export function roleColumnLevel(
  r: RoleCatalogEntry,
  intakeOrder: number,
): RoleColumnLevel {
  return Number.isFinite(intakeOrder) && orderOf(r) === intakeOrder
    ? "lead"
    : "deal";
}

/** The first-stage order of the earliest-starting working role. */
export function intakeStageOrder(catalog: readonly RoleCatalogEntry[]): number {
  let min = Number.POSITIVE_INFINITY;
  for (const r of catalog) {
    if (!isWorkingRole(r)) continue;
    min = Math.min(min, orderOf(r));
  }
  return min;
}

/** The catalog, deduped by id and in journey order. */
export function sortRoleCatalog<T extends RoleCatalogEntry>(
  rolesPipeline: readonly T[] | null | undefined,
): T[] {
  const byId = new Map<number, T>();
  for (const r of rolesPipeline ?? []) {
    const id = roleId(r);
    if (id > 0 && !byId.has(id)) byId.set(id, r);
  }
  return Array.from(byId.values()).sort(compareRolesByJourney);
}

/**
 * Roles a list shows when NOTHING is ticked: the working roles. A tenant whose
 * roles carry no per-stage config at all (pre-wizard) falls back to every role
 * that can receive an assignment, minus the top of the hierarchy (the admin).
 *
 * Roles the viewer EXCLUDED are never part of a default — the picker lists
 * them (so anyone can add the column deliberately) but they must not appear
 * unasked, which is the standing "excluded roles stay out of the automatic
 * surfaces" rule. An explicit tick bypasses this entirely.
 */
export function defaultRoleColumnsCatalog<T extends RoleCatalogEntry>(
  catalog: readonly T[],
): T[] {
  const included = catalog.filter((r) => r.isExcluded !== true);
  const working = included.filter(isWorkingRole);
  if (working.length > 0) return working;
  let topHierarchy = Number.POSITIVE_INFINITY;
  for (const r of included) topHierarchy = Math.min(topHierarchy, hierarchyOf(r));
  return included.filter(
    (r) =>
      (r.acceptsLead !== false || r.acceptsChild !== false) &&
      hierarchyOf(r) !== topHierarchy,
  );
}

function toColumn(r: RoleCatalogEntry, intakeOrder: number): RoleColumn {
  const id = roleId(r);
  return {
    id,
    key: `role-${id}`,
    label: String(r.name ?? `Role ${id}`),
    level: roleColumnLevel(r, intakeOrder),
  };
}

/**
 * The role columns a list should render for this user's Show/hide state.
 * `roleToggles` is the slice map (`{ "<roleId>": true }`); an empty map means
 * "the working-role default".
 */
export function resolveRoleColumns(
  rolesPipeline: readonly RoleCatalogEntry[] | null | undefined,
  roleToggles: Record<string, boolean> | null | undefined,
): RoleColumnLayout {
  const catalog = sortRoleCatalog(rolesPipeline);
  if (catalog.length === 0) return EMPTY_LAYOUT;

  const ticked = new Set(
    Object.entries(roleToggles ?? {})
      .filter(([, on]) => Boolean(on))
      .map(([id]) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0),
  );
  const explicit = catalog.filter((r) => ticked.has(roleId(r)));
  const isDefault = explicit.length === 0;
  const chosen = isDefault ? defaultRoleColumnsCatalog(catalog) : explicit;
  if (chosen.length === 0) return { ...EMPTY_LAYOUT, isDefault };

  // The intake order comes from the WHOLE catalog, not the chosen subset —
  // ticking only the visa team must not promote it to the lead group.
  const intakeOrder = intakeStageOrder(catalog);
  const all = chosen.map((r) => toColumn(r, intakeOrder));
  return {
    lead: all.filter((c) => c.level === "lead"),
    deal: all.filter((c) => c.level === "deal"),
    all,
    ids: all.map((c) => c.id).sort((a, b) => a - b),
    isDefault,
  };
}

/** `roleShow` query value for a layout ("" when there is nothing to attach). */
export function roleShowParam(layout: RoleColumnLayout): string {
  return layout.ids.length > 0 ? layout.ids.join(",") : "";
}
